// Слова и адреса вкладки «Расчёт сотрудника». Чистые функции: без React и без запросов, чтобы
// каждую фразу и каждый адрес можно было проверить без экрана.
//
// Все числа и слова документа приходят с сервера готовыми: он посчитал и сложил деньги, назвал
// пробелы и предупреждения. Здесь только то, что принадлежит кабинету: заголовок документа,
// адреса экранов, куда ведут шаги, и порядок людей в выборе.

import type { EmployeeOut } from '@/shared/api/endpoints/people';
import type {
  PayslipPeriodBlock,
  PayslipPeriodGap,
  PayslipPeriodOut,
  PayslipPeriodRange,
  PayslipPeriodStep,
} from '@/shared/api/endpoints/payslip';
import { moreHours } from '@/shared/hours/format';
import { formatDateOnly } from '@/shared/time/format';
import type { Period } from '@/shared/time/period';
import {
  formatPeriodKey,
  formatPeriodTitle,
  месяцВРодительном,
  shiftPeriod,
} from '@/shared/time/period';

// ── Границы периода ───────────────────────────────────────────────────────────────────────

/** Сколько месяцев сервер считает за один раз. То же число, что `ПОТОЛОК_МЕСЯЦЕВ` в его расчёте. */
export const ПОТОЛОК_МЕСЯЦЕВ = 60;

/** Какую границу двигают: начало («с») или конец («по»). */
export type Граница = 'от' | 'до';

/** Новый месяц границы либо причина, по которой шаг не делается. Молча не отказываем никогда. */
export type ШагГраницы = { период: Period } | { запрет: string };

function номерМесяца(период: Period): number {
  return период.year * 12 + период.month;
}

/**
 * Шаг стрелкой у одной из границ периода.
 *
 * Не делается, если период вывернулся бы (начало позже конца), вышел бы за текущий месяц (месяца,
 * который ещё не кончился, расчёт не знает: ведомости за него нет) или стал бы длиннее, чем
 * сервер считает за один раз. В каждом случае причина называется словами и в ней же сказано, что
 * сделать: серая стрелка без объяснения не доводит нажатие до обработчика и читается как
 * поломка (`.claude/rules/ui-views.md`, «Гейт без объяснения»).
 */
export function шагГраницы(
  какая: Граница,
  шаг: -1 | 1,
  от: Period,
  до: Period,
  сегодня: Period,
): ШагГраницы {
  const новое = shiftPeriod(какая === 'от' ? от : до, шаг);
  const новоеНачало = какая === 'от' ? новое : от;
  const новыйКонец = какая === 'до' ? новое : до;
  if (номерМесяца(новое) === номерМесяца(какая === 'от' ? от : до)) {
    // Край календаря: дальше года, который он знает, не листается.
    return { запрет: 'Дальше этого месяца календарь не листается.' };
  }
  if (номерМесяца(новоеНачало) > номерМесяца(новыйКонец)) {
    return {
      запрет:
        какая === 'от'
          ? 'Начало не может быть позже конца: сначала сдвиньте конец периода вперёд.'
          : 'Конец не может быть раньше начала: сначала сдвиньте начало периода назад.',
    };
  }
  if (какая === 'до' && номерМесяца(новыйКонец) > номерМесяца(сегодня)) {
    return { запрет: 'Дальше текущего месяца расчёта не бывает: ведомостей за него ещё нет.' };
  }
  if (номерМесяца(новыйКонец) - номерМесяца(новоеНачало) + 1 > ПОТОЛОК_МЕСЯЦЕВ) {
    return {
      запрет: `За один раз считается не больше ${ПОТОЛОК_МЕСЯЦЕВ} месяцев: сначала сдвиньте другую границу.`,
    };
  }
  return { период: новое };
}

/** Границы документа как месяцы, а не как четыре числа: так их показывают календари. */
export function началоПериода(период: PayslipPeriodRange): Period {
  return { year: период.year_from, month: период.month_from };
}

export function конецПериода(период: PayslipPeriodRange): Period {
  return { year: период.year_to, month: период.month_to };
}

/**
 * Период словами: «с ноября 2025 по март 2026», а для одного месяца «за март 2026».
 *
 * После «по» стоит винительный, а у месяцев он совпадает с именительным, поэтому «по март»,
 * а не «по марта».
 */
export function фразаПериода(период: PayslipPeriodRange): string {
  const начало = началоПериода(период);
  const конец = конецПериода(период);
  if (formatPeriodKey(начало) === formatPeriodKey(конец)) {
    return `за ${formatPeriodTitle(начало).toLowerCase()}`;
  }
  return `с ${месяцВРодительном(начало)} по ${formatPeriodTitle(конец).toLowerCase()}`;
}

/**
 * Заголовок документа: «Расчёт: Шаврин Виктор Иванович, с ноября 2025 по март 2026, уволен
 * 29.03.2026». Без периода (ведомостей нет) и без увольнения части просто нет.
 */
export function заголовокРасчёта(данные: PayslipPeriodOut): string {
  const части = [`Расчёт: ${данные.person.name}`];
  if (данные.period !== null && данные.period !== undefined) {
    части.push(фразаПериода(данные.period));
  }
  if (данные.person.fired_at !== null && данные.person.fired_at !== undefined) {
    части.push(`уволен ${formatDateOnly(данные.person.fired_at)}`);
  }
  return части.join(', ');
}

/**
 * Часы без нулевой дроби: «96.00» → «96», «12.50» → «12,5». Пусто — прочерк: часы, которые не
 * проставили, не то же самое, что ноль часов.
 */
export function часыТекстом(значение: string | null | undefined): string {
  if (значение === null || значение === undefined || значение === '') {
    return '—';
  }
  const [целая, дробь = ''] = значение.split('.');
  const хвост = дробь.replace(/0+$/, '');
  return хвост === '' ? (целая ?? '—') : `${целая ?? ''},${хвост}`;
}

// ── Адреса, куда ведут шаги пробелов ──────────────────────────────────────────────────────

function месяцАдреса(год: number, месяц: number): string {
  return formatPeriodKey({ year: год, month: месяц });
}

/**
 * Создать ведомость объекта за месяц: панель создания открывается поверх вкладки «Ведомости»
 * сразу с отмеченным объектом. Путь ведомостей написан полностью, а не берётся от текущего:
 * ссылки внутри панели строятся от адреса, на котором она открыта, и с этой вкладки увели бы
 * не туда.
 */
export function адресСозданияВедомости(год: number, месяц: number, объект: string): string {
  const ключ = месяцАдреса(год, месяц);
  return `/payroll/sheets?f.period=${ключ}&p=sheet-generate:${ключ}~${encodeURIComponent(объект)}`;
}

/** Табель объекта за месяц. Месяц пишется всегда: без него табель открылся бы на текущем. */
export function адресТабеля(год: number, месяц: number, объект: string): string {
  return `/payroll/timesheet?f.period=${месяцАдреса(год, месяц)}&f.site=${encodeURIComponent(объект)}`;
}

/** Ведомость за месяц, открытая на своей странице. */
export function адресВедомости(год: number, месяц: number, ведомость: string): string {
  return `/payroll/sheets?f.period=${месяцАдреса(год, месяц)}&f.sheet=${encodeURIComponent(ведомость)}`;
}

/** Добавить человека в готовую ведомость: панель добавления открывается поверх неё. */
export function адресДобавленияВВедомость(год: number, месяц: number, ведомость: string): string {
  return `${адресВедомости(год, месяц, ведомость)}&p=sheet-add:${encodeURIComponent(ведомость)}`;
}

/** Куда идти по шагу пробела. `null` — шаг не к чему привязать (нет объекта или ведомости). */
export function адресШага(шаг: PayslipPeriodStep): string | null {
  switch (шаг.kind) {
    case 'create_payroll':
      return шаг.site_id === null || шаг.site_id === undefined
        ? null
        : адресСозданияВедомости(шаг.year, шаг.month, шаг.site_id);
    case 'open_timesheet':
      return шаг.site_id === null || шаг.site_id === undefined
        ? null
        : адресТабеля(шаг.year, шаг.month, шаг.site_id);
    case 'add_person':
      return шаг.payroll_id === null || шаг.payroll_id === undefined
        ? null
        : адресДобавленияВВедомость(шаг.year, шаг.month, шаг.payroll_id);
    case 'recalculate_payroll':
      // Не переход, а действие: кабинет рисует кнопку, и адреса у шага нет.
      return null;
  }
}

// ── «Внести часы» ─────────────────────────────────────────────────────────────────────────

/** Неразрывный пробел: между числом и единицей перенос строки не ставится. */
const NBSP = '\u00a0';

/**
 * Сколько часов бывает в месяце самое большее: сутки на число дней. Тот же потолок, что и у
 * сервера: он отказывает выше него, и лучше сказать об этом до отправки, а не отказом после.
 */
export function потолокЧасов(период: Period): number {
  // День «ноль» следующего месяца — последний день этого: так число дней не зависит от пояса.
  return new Date(период.year, период.month, 0).getDate() * 24;
}

export type РазборЧасов = { часы: string } | { ошибка: string };

/**
 * Часы из поля ввода: число до сотых, запятая или точка, не больше суток на число дней месяца.
 *
 * Больше НОРМЫ здесь не проверяется и отказом не бывает: человек мог отработать сверх неё, и
 * это предупреждение (`большеНормы`), а не ошибка. Пустое поле не превращается в ноль: ноль
 * вносят явно, потому что «не выходил» и «не заполнено» разные факты.
 */
export function разобратьЧасы(текст: string, период: Period): РазборЧасов {
  const сырой = текст.trim();
  if (сырой === '') {
    return { ошибка: 'Впишите часы за месяц: например 60 или 60,5.' };
  }
  if (сырой.startsWith('-')) {
    return { ошибка: 'Часы не бывают отрицательными: впишите, сколько человек отработал.' };
  }
  if (!/^\d+(?:[.,]\d+)?$/.test(сырой)) {
    return { ошибка: 'Часы вводятся числом: 60 или 60,5.' };
  }
  const часы = сырой.replace(',', '.');
  const дробь = часы.split('.')[1] ?? '';
  if (дробь.replace(/0+$/, '').length > 2) {
    return { ошибка: 'Часы вводятся с точностью до сотых: 60,25.' };
  }
  const потолок = потолокЧасов(период);
  if (moreHours(часы, String(потолок))) {
    return {
      ошибка: `За ${formatPeriodTitle(период).toLowerCase()} не бывает больше ${потолок} часов: проверьте число.`,
    };
  }
  return { часы };
}

/** Больше нормы по назначению. Нормы нет — сравнивать не с чем, и предупреждения тоже нет. */
export function большеНормы(часы: string, норма: string | null | undefined): boolean {
  if (норма === null || норма === undefined || норма === '') {
    return false;
  }
  return moreHours(часы, норма);
}

/**
 * Подпись внутри поля часов, справа: «из нормы 372 ч». Нормы у назначения нет — «нормы нет», а не
 * пусто и не ноль: без нормы человек не знает, много он вносит или мало.
 *
 * Стоит внутри поля, а не под ним: в строке пробела на планшете под полем места нет, а норма
 * нужна именно в момент ввода числа.
 */
export function суффиксНормы(норма: string | null | undefined): string {
  return норма === null || норма === undefined || норма === ''
    ? 'нормы нет'
    : `из нормы ${часыТекстом(норма)}${NBSP}ч`;
}

/**
 * Вопрос подтверждения: «Внести 60 ч из 372 за сентябрь 2026 по объекту «Заря»?». Часы уже
 * внесены — «Заменить 50 ч на 60 ч …»: человек видит, что будет перезаписано, а не только новое.
 */
export function фразаПодтверждения(параметры: {
  часы: string;
  норма: string | null | undefined;
  прежние: string | null | undefined;
  период: Period;
  объект: string;
}): string {
  const { часы, норма, прежние, период, объект } = параметры;
  // Число с единицей не рвётся на две строки: «60» на одной, «ч» на другой читается как опечатка.
  const из =
    норма === null || норма === undefined || норма === '' ? '' : ` из${NBSP}${часыТекстом(норма)}`;
  const где = `за ${formatPeriodTitle(период).toLowerCase()} по объекту «${объект}»`;
  if (прежние !== null && прежние !== undefined && прежние !== '') {
    return `Заменить ${часыТекстом(прежние)}${NBSP}ч на ${часыТекстом(часы)}${NBSP}ч${из} ${где}?`;
  }
  return `Внести ${часыТекстом(часы)}${NBSP}ч${из} ${где}?`;
}

// ── Пробелы документа: слова строки и общий текст ─────────────────────────────────────────

/**
 * Строка пробела: «Май 2026, «БурСервис Северный»: табель не сдан». Месяц, объект и причина
 * коротко, как в листе, где пробелы идут столбиком: полная фраза сервера («Табель за май 2026 по
 * объекту «БурСервис Северный» не сдан») на планшете занимала две строки на каждый месяц.
 *
 * Когда объект у всех пробелов один, он назван над списком один раз, и в строке стоит только месяц
 * и причина (`безОбъекта`): семь раз подряд «БурСервис Северный» занимали полстроки, на которую
 * не оставалось места полю часов и кнопке.
 *
 * Фраза собрана из тех же полей, что и слова сервера, а вид, которого здесь не знают, показывается
 * словами сервера как есть: новая причина не должна пропасть из документа молча.
 */
export function фразаПробела(пробел: PayslipPeriodGap, безОбъекта = false): string {
  const месяц = formatPeriodTitle({ year: пробел.period_year, month: пробел.period_month });
  const где =
    безОбъекта ||
    пробел.site_name === null ||
    пробел.site_name === undefined ||
    пробел.site_name === ''
      ? месяц
      : `${месяц}, «${пробел.site_name}»`;
  switch (пробел.kind) {
    case 'no_timesheet':
      return `${где}: табель${NBSP}не${NBSP}сдан`;
    case 'no_payroll':
      return `${где}: ведомости${NBSP}нет`;
    case 'not_in_payroll':
      return `${где}: человека${NBSP}нет${NBSP}в${NBSP}ведомости`;
    case 'hours_not_in_payroll':
      return пробел.timesheet_hours === null || пробел.timesheet_hours === undefined
        ? `${где}: часы внесены в табель`
        : `${где}: часы внесены в табель, ${часыТекстом(пробел.timesheet_hours)}${NBSP}ч`;
    default:
      return пробел.text;
  }
}

/** Предложения подсказки: граница это точка, пробел и заглавная; точка внутри числа и даты не режет. */
function предложения(текст: string): string[] {
  return текст
    .split(/\.\s+(?=[А-ЯЁA-Z])/)
    .map((часть) => часть.trim())
    .filter((часть) => часть !== '')
    .map((часть) => (часть.endsWith('.') ? часть : `${часть}.`));
}

/** «Норма по назначению: 540 ч.» уже стоит в поле числом «из нормы 540 ч», и второй раз её не пишем. */
const ЭТО_НОРМА = /^Норма по назначению:\s*[\d.,]+\s*ч\.?$/;

export interface РазборПодсказок {
  /** Что сказать один раз над списком пробелов. В порядке первого появления. */
  общие: string[];
  /** Что принадлежит одной строке: у остальных этого нет. */
  личные: Map<PayslipPeriodGap, string[]>;
}

/**
 * Подсказки пробелов, разложенные на общие и личные.
 *
 * Подсказка, сказанная дословно у нескольких пробелов («Внесите часы за месяц итогом»), читается
 * один раз над списком: читать её по разу под каждым месяцем значит искать разницу, которой нет.
 * Предложение, которое есть только у одной строки («Человек на объекте по 12.05.2026»), остаётся
 * при ней. Пробел один — всё сказанное им общее, а не личное.
 *
 * Делится по предложениям, а не по целой подсказке: у месяцев без табеля она отличается только
 * нормой и датой ухода, а остальное слово в слово.
 */
export function разобратьПодсказки(пробелы: readonly PayslipPeriodGap[]): РазборПодсказок {
  const частоты = new Map<string, number>();
  const порядок: string[] = [];
  const пофразно = new Map<PayslipPeriodGap, string[]>();
  for (const пробел of пробелы) {
    const фразы = [
      ...new Set(предложения(пробел.hint ?? '').filter((фраза) => !ЭТО_НОРМА.test(фраза))),
    ];
    пофразно.set(пробел, фразы);
    for (const фраза of фразы) {
      if (!частоты.has(фраза)) {
        порядок.push(фраза);
      }
      частоты.set(фраза, (частоты.get(фраза) ?? 0) + 1);
    }
  }
  const общая = (фраза: string) => пробелы.length === 1 || (частоты.get(фраза) ?? 0) > 1;
  const личные = new Map<PayslipPeriodGap, string[]>();
  for (const [пробел, фразы] of пофразно) {
    личные.set(
      пробел,
      фразы.filter((фраза) => !общая(фраза)),
    );
  }
  return { общие: порядок.filter(общая), личные };
}

// ── Что в итоге не посчитано ──────────────────────────────────────────────────────────────

/**
 * Деньги блока НЕИЗВЕСТНЫ, а не нулевые: ведомость ещё в работе, а расчёт по ней не проводился
 * (нет тарифа, часы не проставлены, норма невозможна). Ноль в таком блоке читается как «ничего не
 * положено», а это неправда: по этому документу решают, сколько платить.
 *
 * Закрытая ведомость сюда не относится, хотя `calculated` у неё тоже ложь: месяцы из майской книги
 * заказчика перенесены закрытыми и выплаченными, расчёт программой по ним не проводился, и «расчёт
 * ещё не проводился» про них говорит сервер. Но деньги в них записаны и остаток по ним ноль: на
 * боевых данных 01.10.2026 так выглядит каждый перенесённый май. Принять их за «неизвестно» значило
 * бы закрасить прочерками итог человека, у которого всё выплачено.
 */
export function суммыНеизвестны(блок: PayslipPeriodBlock): boolean {
  return !блок.calculated && !блок.is_final;
}

export interface НепосчитанноеВБлоках {
  /** Блоков в группе (по одному юрлицу), всех. */
  всего: number;
  /** Из них в работе и без расчёта: денег по ним нет не потому, что ноль. */
  без: number;
  /** Причины словами сервера, без повторов, в порядке появления. */
  причины: string[];
}

/** Сколько блоков группы без расчёта и почему. Причина у многих одна и называется один раз. */
export function непосчитанноеВБлоках(блоки: readonly PayslipPeriodBlock[]): НепосчитанноеВБлоках {
  const без = блоки.filter(суммыНеизвестны);
  const причины: string[] = [];
  for (const блок of без) {
    const причина = блок.not_counted?.trim();
    if (причина !== undefined && причина !== '' && !причины.includes(причина)) {
      причины.push(причина);
    }
  }
  return {
    всего: блоки.length,
    без: без.length,
    причины,
  };
}

/**
 * Итог неизвестен: есть ведомости в работе без расчёта, а в остатке нет ни одного известного
 * положительного числа. Тогда «0» в «Осталось выплатить» ничего не говорит: это не «платить
 * нечего», а «посчитать нечем».
 *
 * Если известная часть остатка есть (утверждённая и невыплаченная ведомость, посчитанный
 * черновик), числа остаются: они правдивы о посчитанном, а сколько ведомостей в них не вошло, сказано
 * рядом. Закрытые из книги месяцы в этот счёт не идут: они выплачены, остаток по ним ноль.
 */
export function итогНеизвестен(
  непосчитано: НепосчитанноеВБлоках,
  итог: { remaining: number },
): boolean {
  return непосчитано.без > 0 && итог.remaining === 0;
}

/** Название и пояснение пятого члена цепочки итога: число без знака, знак в подписи. */
export interface ПоправкаИтога {
  подпись: string;
  пояснение: string;
}

const ПО_ЗАМЕЧАНИЯМ = 'подробности в замечаниях к ведомостям';

/**
 * Как назвать разницу между итогом ведомостей и суммой их строк.
 *
 * Причин у плюса две, и называть их одним словом нельзя. «Не поместилось в начисление» верно, когда
 * вычеты оказались больше заработанного и остаток аванса стал долгом (каскад удержаний): у Шавриных
 * 62 144 − 77 140 + 38 570 = 23 574. Но у месяца, перенесённого из майской книги, итог бывает
 * больше строк просто потому, что так записано в книге, а вычеты нули: на боевых данных 01.10.2026
 * у человека с нулевыми вычетами цепочка читалась «+ Не поместилось в начисление 3 000, вычеты больше
 * начисленного», то есть объясняла несуществующее. Различает сервер: у блока каскада стоит
 * `zeroed_by_cascade`. Блоков с разными причинами в одном итоге несколько, и тогда подпись общая.
 */
export function поправкаИтога(
  поправка: number,
  блоки: readonly PayslipPeriodBlock[],
): ПоправкаИтога {
  if (поправка < 0) {
    return {
      подпись: '− Удержано сверх строк',
      пояснение: `из итога удержано больше, чем показано строками; ${ПО_ЗАМЕЧАНИЯМ}`,
    };
  }
  const каскад = блоки.some((блок) => блок.adjustment > 0 && блок.zeroed_by_cascade);
  const прочие = блоки.some((блок) => блок.adjustment > 0 && !блок.zeroed_by_cascade);
  if (каскад && !прочие) {
    return {
      подпись: '+ Не поместилось в начисление',
      пояснение: `вычеты больше начисленного; ${ПО_ЗАМЕЧАНИЯМ}`,
    };
  }
  if (прочие && !каскад) {
    return {
      подпись: '+ Итог больше строк',
      пояснение: `итог ведомости больше, чем получается из строк: расчёт стоит проверить; ${ПО_ЗАМЕЧАНИЯМ}`,
    };
  }
  return {
    подпись: '+ Поправка к итогу',
    пояснение: `итог отличается от суммы строк (вычеты не поместились в начисление или итог больше строк); ${ПО_ЗАМЕЧАНИЯМ}`,
  };
}

/** Первая буква заглавная: причина сервера пишется со строчной («не заполнен тариф: …»). */
export function сЗаглавной(текст: string): string {
  return текст === '' ? текст : `${текст.charAt(0).toUpperCase()}${текст.slice(1)}`;
}

// ── Люди для выбора ───────────────────────────────────────────────────────────────────────

/**
 * Уволенные первыми, внутри обеих групп порядок прежний (по фамилии, как отдал сервер).
 *
 * Расчёт нужен прежде всего тому, кто уходит или ушёл, и искать его среди трёхсот работающих
 * нельзя. В общем выборе людей (`shared/people/lookup.ts`) уволенные стоят в конце, и там это
 * верно: старшему объекта он чаще ошибка, чем цель. Здесь наоборот.
 */
export function уволенныеПервыми(люди: readonly EmployeeOut[]): EmployeeOut[] {
  const уволенные = люди.filter((человек) => человек.status === 'fired');
  const остальные = люди.filter((человек) => человек.status !== 'fired');
  return [...уволенные, ...остальные];
}

/**
 * Подпись человека в списке выбора под именем: где стоит или стоял.
 *
 * Про увольнение здесь ничего: оно уже написано меткой справа, и одно и то же дважды в одной
 * строке читается как ошибка.
 */
export function подписьЧеловека(человек: EmployeeOut): string {
  return человек.site_name ?? человек.city ?? '';
}
